import React from 'react';
import { Box } from '@mui/material';
import { MuiButton } from '@ui/base_library';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';

interface SingleSelectMenuButtonProps {
  label: string;
  icon?: AvailableIconTypes;
  imgSrc?: string;
  ariaLabel: string;
  active?: boolean;
  /**
   * Renders elements to the right of the displayed text label
   *
   */
  isFavorite?: boolean;
  children?: React.ReactNode;
  className?: string;
  /** Ensures the trigger is in the tab order for keyboard and screen reader access */
  tabIndex?: number;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
}

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()(theme => ({
  menuTrigger: {
    textTransform: 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '32px',
    height: 'auto',
    borderRadius: theme.spacing(0.5),
    outline: `1px solid ${theme.palette.inputs.default}`,
    padding: theme.spacing(1, 1, 1, 1.5),
    fontWeight: 'normal',
    color: palette.gray_0,
    cursor: 'pointer',
    lineHeight: 'normal',
    '&:hover': {
      outline: `1px solid ${theme.palette.inputs.active}`,
    },
    '&[data-disabled]': {
      color: palette.gray_300,
      outline: `1px solid ${palette.gray_700}`,
      backgroundColor: palette.gray_800,
    },
  },
  label: {
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 'inherit',
    overflowWrap: 'anywhere',
  },
  favorite: {
    color: palette.punch_400,
    marginRight: theme.spacing(0.5),
  },
  icon: {
    marginLeft: 'auto',
    marginRight: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
}));

/**
 * Main button to display the currently selected option
 * Used with menu
 */
const MenuButton = React.forwardRef<
  HTMLButtonElement,
  SingleSelectMenuButtonProps
>((props, forwardRef) => {
  const {
    label,
    ariaLabel,
    icon,
    imgSrc,
    isFavorite,
    children,
    className,
    ...rest
  } = props;
  const { classes, cx } = useStyles();
  return (
    <MuiButton
      type="button"
      {...rest}
      ref={forwardRef}
      aria-label={ariaLabel}
      className={cx(classes.menuTrigger, className)}
    >
      {imgSrc && (
        <Box display="flex" alignItems="center" flexShrink={0} mr={1}>
          <img src={imgSrc} alt="" style={{ width: '16px', height: '16px' }} />
        </Box>
      )}
      {icon && !imgSrc && (
        <Box display="flex" alignItems="center" flexShrink={0} mr={1}>
          <Icon type={icon} />
        </Box>
      )}
      <Typography variant="pSm" component="span" className={classes.label}>
        {label}
      </Typography>

      {children}
    </MuiButton>
  );
});

export default MenuButton;
